import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Grid
} from '@mui/material'
import { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useQuery } from '@tanstack/react-query'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import ColorBox from 'common/Box/ColorWhiteBox'
import NormalText from 'common/Typography/NormalText'
import { changeMonth } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'
import { ViewProfileButton } from 'pages/Customers/sharedComponents'
import { SearchToolbar } from 'pages/OTPacks/sharedComponents'
import { GetAllFaberUsersDetails } from 'types/api/faber/oneTimePack.type'

import StatsChart from './StatsChart'
export default function RevampedFaberCustomerDashboard() {
  const navigate = useNavigate()
  const role = OrbitSession.get('role')

  const [rows, setRows] = useState<GetAllFaberUsersDetails[]>([])
  const [statusChecked] = useState<string[]>([])
  const [bundleChecked] = useState<string[]>([])

  const [tempSearchValue, setTempSearchValue] = useState<string>('')
  // hard code the start date to before launch date
  const [startDate] = useState<Date>(new Date(2022, 9, 1))
  const [endDate] = useState<Date>(changeMonth(new Date(), +3))
  const [sortDate, setSortDate] = useState<string>('DESC')
  const [searchValue, setSearchValue] = useState<string>('')
  const [searchCriteria, setSearchCriteria] = useState<string>('Email')

  const [pageOffset, setPageOffset] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize] = useState(10)
  // TODO: TO UNCOMMENT WHEN API RETURNS CORRECT FIELDS FOR PAGE 2 ONWARDS
  const [rowCount, setRowCount] = useState(0)

  const [breakdown, setBreakdown] = useState<number[]>([])
  const [total, setTotal] = useState(0)
  const [isTotalsLoading, setIsTotalsLoading] = useState<boolean>(false)
  // const [plansBreakdown, setPlansBreakdown] = useState<number[]>([])
  // const [plansTitle, setPlansTitle] = useState<string[]>([])
  // const [plansTotal, setPlansTotal] = useState(0)

  const faberToken = OrbitSession.get('faberToken')

  const searchOptions = ['Email', 'MSISDN', 'ICCID', 'Customer ID']
  const titles = [
    'Total Customers',
    'Lite Pac Active',
    'Pro Pac Active',
    'Tourist',
    'Backpacker',
    'Wanderer',
    'Jetsetter',
    'Globetrotter'
  ]

  const queryOptions = React.useMemo(
    () => ({
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      filterStartDate: startDate,
      filterEndDate: endDate,
      sortDate
    }),
    [
      pageOffset,
      pageSize,
      statusChecked,
      bundleChecked,
      searchCriteria,
      searchValue,
      startDate,
      endDate,
      sortDate
    ]
  )

  const handlePageChange = (newPage: number) => {
    const tmpOffset = newPage * pageSize
    setPageOffset(tmpOffset / 10)
    setPage(newPage)
  }

  function resetPage() {
    setPageOffset(0)
    setPage(0)
  }

  const handleSortModelChange = React.useCallback(
    (sortModel: GridSortModel) => {
      if (sortModel[0].sort === 'asc') {
        setSortDate('ASC')
      } else {
        setSortDate('DESC')
      }
    },
    []
  )

  const { data: getAllUsers, isLoading: isGetAllUsersLoading } = useQuery(
    ['getAllUsers', [queryOptions]],
    async () =>
      queryOptions.searchValue.length > 0
        ? OneTimePackApi.getAllFaberUsers({
          faberToken: faberToken,
          searchCriteria: queryOptions.searchCriteria,
          searchValue: queryOptions.searchValue,
          limit: 10,
          planStatus: 'active',
          offset: queryOptions.pageOffset
        })
        : null,
    {
      onSuccess: (res) => {
        return res
      },
      onError: () => {
        setRows([])
      }
    }
  )

  useEffect(() => {
    if (getAllUsers) {
      const { data, total } = getAllUsers.data.body
      const searchedSubscriptions: GetAllFaberUsersDetails[] = data.map(
        ({
          _id,
          user_id,
          user_metadata,
          email,
          contactNo,
          address,
          createdAt,
          firstName,
          lastName,
          role,
          showPlanPage,
          showTutorial,
          sin,
          status,
          updatedAt,
          subscription,
          otpack
        }) => ({
          _id,
          user_id,
          user_metadata,
          contactNo,
          firstName,
          email,
          msisdn: contactNo.number,
          address,
          createdAt,
          lastName,
          role,
          showPlanPage,
          showTutorial,
          sin,
          status,
          updatedAt,
          subscription,
          otpack
        })
      )

      setRows(searchedSubscriptions)
      setRowCount(total)
    }
  }, [getAllUsers])
  //search values
  const onSearchValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setTempSearchValue(e.target.value)
  }

  const onSearchSubmit = () => {
    setSearchValue(tempSearchValue)
    resetPage()
  }

  const handleSearchCriteriaChange = (e, v: string) => {
    setSearchCriteria(v)
  }

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: 'Name',
      width: 200,
      renderCell: (params: { row: GetAllFaberUsersDetails }) => {
        return (
          <NormalText>
            {params.row.firstName + ' ' + params.row.lastName}
          </NormalText>
        )
      }
    },
    { field: '_id', headerName: 'Customer ID', width: 150 },
    { field: 'msisdn', headerName: 'Contact Number', width: 50 },

    {
      field: 'email',
      headerName: 'Email',
      width: 120
    },
    { field: 'sin', headerName: 'SIN', width: 50 },
    {
      field: 'viewProfile',
      flex: 0.75,
      minWidth: 90,
      filterable: false,
      sortable: false,
      headerName: 'View Profile',
      renderCell: (params: { row: GetAllFaberUsersDetails }) => {
        return role !== 'opsviewer' ? (
          <ViewProfileButton customerRow={params.row} />
        ) : (
          ''
        )
      }
    }
  ]

  //FOR GETTING STATS CARDS
  const { data: getSubscriptionTotals, refetch: refetchSubscriptionTotals } =
    useQuery(
      ['getSubscriptionTotals'],
      async () => {
        return SubscriptionsAPI.getSubscriptionTotals(faberToken)
      },
      {
        onError: () => {
          toast.error('Error getting subscription totals')
          setRows([])
        }
      }
    )

  // const { refetch: refetchOTPPlansTotal } = useQuery(
  //   ['getOTPPlansTotals'],
  //   async () => {
  //     const oneTimePackPlansRes = await OneTimePackApi.getAllUserPlans(
  //       faberToken
  //     )
  //     return oneTimePackPlansRes.data
  //   },
  //   {
  //     onError: () => {
  //       toast.error('Error getting one time pack totals')
  //     },
  //     onSuccess: (res) => {
  //       const allTitles = res.data.grouped.map((data) => {
  //         return data.planName
  //       })
  //       const allCount = res.data.grouped.map((data) => {
  //         return data.count
  //       })
  //       setPlansTotal(res.data.total)
  //       setPlansTitle(allTitles)
  //       setPlansBreakdown(allCount)
  //     }
  //   }
  // )

  useEffect(() => {
    if (getSubscriptionTotals) {
      const { breakdown, total } = getSubscriptionTotals.data.data
      const breakDownStats = breakdown.map((item) => {
        return item.count
      })
      setBreakdown(breakDownStats)
      setTotal(total)
    } else {
      setBreakdown([])
    }
  }, [getAllUsers, getSubscriptionTotals])

  useEffect(() => {
    refetchSubscriptionTotals()
    // refetchOTPPlansTotal()
  }, [])
  const newTitles = titles.filter((_, index: number) => index !== 0)

  const subscriptionStatsData: StatsData = useMemo(() => {
    return {
      labels: newTitles,
      datasets: [
        {
          label: 'Subscription',
          data: breakdown,
          borderColor: 'transparent',
          backgroundColor: 'rgba(164, 170, 255, 0.5)'
        }
      ]
    }
  }, [newTitles, breakdown])

  // TO DO: change when api is ready, currently mock
  // const oneTimePackData: StatsData = useMemo(() => {
  //   return {
  //     labels: plansTitle,
  //     datasets: [
  //       {
  //         label: 'One time packs',
  //         data: plansBreakdown,
  //         borderColor: 'transparent',
  //         backgroundColor: 'rgba(210, 235, 255, 0.8)'
  //       }
  //     ]
  //   }
  // }, [newTitles, breakdown])

  // useEffect(() => {
  //   if ((breakdown && breakdown.length > 0) || plansBreakdown.length > 0) {
  //     setIsTotalsLoading(false)
  //   } else {
  //     setIsTotalsLoading(true)
  //   }
  // }, [breakdown, plansBreakdown])

  useEffect(() => {
    if (breakdown && breakdown.length > 0) {
      setIsTotalsLoading(false)
    } else {
      setIsTotalsLoading(true)
    }
  }, [breakdown])

  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 2, gap: 2 }}>
        {isTotalsLoading ? (
          <CircularProgress />
        ) : (
          <>
            <ColorBox sx={{ width: '100%' }}>
              <NormalText theme='darkPrimary' isBold>
                Subscription breakdown
              </NormalText>

              <Accordion sx={{ boxShadow: 0 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='panel1a-content'
                  id='panel1a-header'
                >
                  <Grid container>
                    <Grid item xs={7}>
                      <NormalText>Total Subscription Users:</NormalText>
                    </Grid>
                    <Grid item xs={5}>
                      <NormalText isBold>{total}</NormalText>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <StatsChart
                    chartTitle='Total Subscription Customers'
                    chartData={subscriptionStatsData}
                  />
                </AccordionDetails>
              </Accordion>
            </ColorBox>

            {/* <ColorBox sx={{ width: '45%' }}>
              <NormalText theme='darkPrimary' isBold>
                One time pack breakdown
              </NormalText>
              <Accordion sx={{ boxShadow: 0 }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls='panel1a-content'
                  id='panel1a-header'
                >
                  <Grid container>
                    <Grid item xs={7}>
                      <NormalText>Total One time pack Users:</NormalText>
                    </Grid>
                    <Grid item xs={5}>
                      <NormalText isBold>{plansTotal}</NormalText>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <StatsChart
                    chartTitle='Total One time pack Customers'
                    chartData={oneTimePackData}
                  />
                </AccordionDetails>
              </Accordion>
            </ColorBox> */}
          </>
        )}
      </Box>
      <ColorBox>
        <Box>
          <NormalText isBold>
            Please type customer details in the search bar to begin
          </NormalText>
          <MUIDataGrid
            getRowId={(row) => `${row.iccid}-${rows.indexOf(row)}`}
            tableHeight='750px'
            rows={rows}
            pagination
            columns={columns}
            paginationMode='server'
            rowCount={rowCount}
            page={page}
            loading={isGetAllUsersLoading}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            disableColumnMenu={true}
            sortingMode='server'
            sortingOrder={['desc', 'asc']}
            onSortModelChange={handleSortModelChange}
            onRowDoubleClick={(params) => {
              {
                navigate(`view-profile/${params.row._id}`, {
                  state: params.row
                })
              }
            }}
            components={{
              Toolbar: SearchToolbar
            }}
            componentsProps={{
              toolbar: {
                searchBar: 'server',
                serverSearchBarProps: {
                  onSubmit: onSearchSubmit,
                  onChange: onSearchValueChange,
                  options: searchOptions,
                  defaultValue: searchCriteria,
                  handleChange: handleSearchCriteriaChange
                }
              }
            }}
          />
        </Box>
      </ColorBox>
    </Box>
  )
}

export interface StatsData {
  labels: string[]
  datasets: {
    label: string
    data: number[]
    borderColor?: string
    backgroundColor?: string
  }[]
}
